import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Edit, FileText, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
} from '@/components/common';
import { suppliersApi, Supplier, SupplierPerformance, PurchaseOrder, purchaseOrdersApi } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const poStatusMap: Record<string, 'draft' | 'pending' | 'approved' | 'processing' | 'completed'> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'draft',
};

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [performance, setPerformance] = useState<SupplierPerformance | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [supplierData, performanceData, posData] = await Promise.all([
        suppliersApi.getById(id),
        suppliersApi.getPerformance(id).catch(() => null),
        purchaseOrdersApi.getAll({ supplierId: id, limit: 10 }),
      ]);
      setSupplier(supplierData);
      setPerformance(performanceData);
      setPurchaseOrders(posData.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!supplier) {
    return null;
  }

  const getRatingBadge = (rating?: number) => {
    if (rating === undefined || rating === null) return <Badge variant="outline">No Rating</Badge>;
    const percentage = rating * 20;
    if (percentage >= 90) return <Badge className="bg-emerald-100 text-emerald-700">Excellent</Badge>;
    if (percentage >= 75) return <Badge className="bg-blue-100 text-blue-700">Good</Badge>;
    if (percentage >= 60) return <Badge className="bg-amber-100 text-amber-700">Average</Badge>;
    return <Badge className="bg-red-100 text-red-700">Needs Improvement</Badge>;
  };

  const poColumns: Column<PurchaseOrder>[] = [
    {
      key: 'poNumber',
      header: 'PO Number',
      render: (po) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{po.poNumber}</span>
        </div>
      ),
    },
    {
      key: 'orderDate',
      header: 'Order Date',
      render: (po) => (
        <span className="text-muted-foreground">{formatDate(po.orderDate)}</span>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (po) => (
        <span className="font-medium">{formatCurrency(po.totalAmount)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (po) => (
        <StatusBadge status={poStatusMap[po.status] || 'draft'} />
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/suppliers')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader
          title={supplier.name}
          description={`Code: ${supplier.code}`}
          breadcrumbs={[
            { label: 'Suppliers', href: '/suppliers' },
            { label: supplier.name },
          ]}
          actions={
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit Supplier
            </Button>
          }
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Orders"
          value={performance?.metrics.totalOrders || supplier._count?.purchaseOrders || 0}
          icon={<FileText className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Value"
          value={formatCurrency(performance?.metrics.totalValue)}
        />
        <MetricCard
          label="On-Time Delivery"
          value={performance?.metrics.onTimeDeliveryRate ? `${performance.metrics.onTimeDeliveryRate}%` : '-'}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <MetricCard
          label="Rating"
          value={supplier.rating ? `${supplier.rating}/5` : '-'}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Supplier Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Supplier Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Status</span>
              <StatusBadge status={supplier.isActive ? 'active' : 'inactive'} />
            </div>
            <Separator />
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Rating</span>
              {getRatingBadge(supplier.rating)}
            </div>
            <Separator />

            {/* Contact Information */}
            {supplier.contactName && (
              <div className="space-y-2">
                <span className="text-sm font-medium">Contact</span>
                <p className="text-muted-foreground">{supplier.contactName}</p>
              </div>
            )}
            {supplier.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${supplier.email}`} className="hover:underline">
                  {supplier.email}
                </a>
              </div>
            )}
            {supplier.phone && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4" />
                <span>{supplier.phone}</span>
              </div>
            )}

            <Separator />

            {/* Address */}
            {supplier.address && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Address</span>
                </div>
                <div className="text-sm text-muted-foreground pl-6">
                  {supplier.address.street && <p>{supplier.address.street}</p>}
                  <p>
                    {[supplier.address.city, supplier.address.state, supplier.address.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {supplier.address.country && <p>{supplier.address.country}</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        {performance && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Historical performance data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">On-Time Delivery Rate</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-semibold">
                      {performance.metrics.onTimeDeliveryRate}%
                    </span>
                    {performance.metrics.onTimeDeliveryRate >= 90 && (
                      <Badge className="bg-emerald-100 text-emerald-700">Excellent</Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Fulfillment Rate</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-semibold">
                      {performance.metrics.fulfillmentRate}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <span className="text-2xl font-semibold">
                    {performance.metrics.totalOrders}
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <span className="text-2xl font-semibold">
                    {formatCurrency(performance.metrics.totalValue)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!performance && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Historical performance data</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">No performance data available yet.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Purchase Orders */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Purchase Orders</CardTitle>
          <CardDescription>Latest orders from this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={purchaseOrders}
            columns={poColumns}
            onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
            emptyTitle="No purchase orders"
            emptyDescription="No orders have been placed with this supplier yet."
          />
        </CardContent>
      </Card>
    </div>
  );
}
